"use client";

import type { EventParams } from "@/utils/analytics";

const HOME_ENTRY_ATTRIBUTION_KEY = "sr_home_entry_attribution_v1";

type HomeEntryAttribution = EventParams & {
  session_source: string;
  session_medium: string;
};

function getReferrerHost() {
  if (typeof document === "undefined" || !document.referrer) return "";
  try {
    return new URL(document.referrer).host;
  } catch {
    return "";
  }
}

function inferSessionSourceMedium(searchParams: URLSearchParams, referrerHost: string) {
  const utmSource = searchParams.get("utm_source")?.trim();
  const utmMedium = searchParams.get("utm_medium")?.trim();
  const utmCampaign = searchParams.get("utm_campaign")?.trim();

  if (utmSource || utmMedium) {
    return {
      session_source: utmSource || "(utm_source_missing)",
      session_medium: utmMedium || "(utm_medium_missing)",
      session_campaign: utmCampaign,
    };
  }

  if (!referrerHost) {
    return {
      session_source: "(direct)",
      session_medium: "(none)",
      session_campaign: undefined,
    };
  }

  return {
    session_source: referrerHost,
    session_medium: "referral",
    session_campaign: undefined,
  };
}

function buildHomeEntryAttribution(): HomeEntryAttribution {
  if (typeof window === "undefined") {
    return { session_source: "(unknown)", session_medium: "(unknown)" };
  }

  const searchParams = new URLSearchParams(window.location.search);
  const referrerHost = getReferrerHost();
  const inferred = inferSessionSourceMedium(searchParams, referrerHost);

  return {
    landing_path: window.location.pathname,
    landing_referrer: typeof document !== "undefined" ? document.referrer || "" : "",
    landing_referrer_host: referrerHost || undefined,
    utm_source: searchParams.get("utm_source") || undefined,
    utm_medium: searchParams.get("utm_medium") || undefined,
    utm_campaign: searchParams.get("utm_campaign") || undefined,
    utm_content: searchParams.get("utm_content") || undefined,
    utm_term: searchParams.get("utm_term") || undefined,
    ...inferred,
  };
}

export function getOrCreateHomeEntryAttribution(): HomeEntryAttribution {
  if (typeof window === "undefined") {
    return { session_source: "(unknown)", session_medium: "(unknown)" };
  }

  const cached = sessionStorage.getItem(HOME_ENTRY_ATTRIBUTION_KEY);
  if (cached) {
    try {
      return JSON.parse(cached) as HomeEntryAttribution;
    } catch {
      sessionStorage.removeItem(HOME_ENTRY_ATTRIBUTION_KEY);
    }
  }

  const next = buildHomeEntryAttribution();
  sessionStorage.setItem(HOME_ENTRY_ATTRIBUTION_KEY, JSON.stringify(next));
  return next;
}
